import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Alert,
  Button,
  Card,
  Spin,
  Typography,
} from 'antd';
import {
  AppstoreOutlined,
  ClockCircleOutlined,
  PlusOutlined,
  SettingOutlined,
} from '@ant-design/icons';
import type { Space as SpaceDto, SpaceListResponse } from '@lens/shared';
import { ApiError } from '../../shared/api/client.ts';
import { listSpaces } from './api.ts';
import { CreateSpaceModal } from './CreateSpaceModal.tsx';
import { SpaceAvatar } from '../../shared/components/SpaceAvatar.tsx';
import { getRecentSpaces } from '../../shared/prefs/store.ts';
import type { RecentSpace } from '../../shared/prefs/store.ts';
import { emitSpacesChanged } from '../../shared/lib/spacesBus.ts';
import { roleBadge } from '../../shared/theme/tokens.ts';
import { useAuth } from '../auth/AuthContext.tsx';
import styles from './SpacesHomePage.module.css';
import { useDocumentTitle } from '../../shared/hooks/useDocumentTitle.ts';

/**
 * Главная страница пространств (M2).
 * Две секции: «Мои пространства» (всегда) и «Доступные пространства»
 * (для platform-админа). Карточки с hover-эффектами.
 */

const ROLE_LABEL: Record<string, string> = {
  admin: 'Администратор',
  member: 'Участник',
  viewer: 'Наблюдатель',
};

interface State {
  data: SpaceListResponse | null;
  loading: boolean;
  error: string | null;
}

function RoleBadge({ role }: { role: string }): JSX.Element {
  const badge = roleBadge[role as keyof typeof roleBadge] ?? roleBadge.viewer;
  return (
    <span
      className={styles.roleBadge}
      style={{ background: badge.bg, color: badge.color }}
    >
      {ROLE_LABEL[role] ?? role}
    </span>
  );
}

function SpaceCard({ space }: { space: SpaceDto }): JSX.Element {
  const navigate = useNavigate();

  const handleCardClick = (e: React.MouseEvent) => {
    // Не перехватываем клики по ссылкам и кнопкам внутри карточки
    const target = e.target as HTMLElement;
    if (target.closest('a') || target.closest('button')) return;
    navigate(`/s/${space.slug}`);
  };

  return (
    <Card
      className={styles.card}
      data-testid={`space-card-${space.slug}`}
      onClick={handleCardClick}
      style={{ cursor: 'pointer' }}
    >
      <div className={styles.cardHead}>
        <div className={styles.cardTitle}>
          <Link
            to={`/s/${space.slug}`}
            className={styles.cardTitleLink}
            title={space.name}
          >
            <SpaceAvatar
              name={space.name}
              icon={space.icon}
              seed={space.id}
              size={20}
              className="lens-space-av-inline"
            />
            <span className={styles.nameText}>{space.name}</span>
          </Link>
        </div>
        <div className={styles.cardActions}>
          {/* Роль показываем только если она ОТЛИЧАЕТСЯ от админа — иначе
              бейдж «Администратор» на каждой карточке = шум. */}
          {space.myRole !== 'admin' ? <RoleBadge role={space.myRole} /> : null}
          {/* Иконка настроек — admin-only (SRS §2.5.2). */}
          {space.myRole === 'admin' ? (
            <Link
              to={`/s/${space.slug}/settings`}
              title="Настройки"
              className={styles.gearLink}
              data-testid={`space-settings-link-${space.slug}`}
            >
              <SettingOutlined />
            </Link>
          ) : null}
        </div>
      </div>
      <span className={styles.slug}>{space.slug}</span>
      {space.description ? (
        <Typography.Paragraph
          className={styles.description}
          ellipsis={{ rows: 2 }}
        >
          {space.description}
        </Typography.Paragraph>
      ) : (
        <div className={styles.descriptionEmpty}>Описание не задано</div>
      )}
    </Card>
  );
}

function AvailableSpaceCard({ space }: { space: SpaceDto }): JSX.Element {
  const navigate = useNavigate();

  const handleCardClick = (e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    if (target.closest('a') || target.closest('button')) return;
    navigate(`/s/${space.slug}`);
  };

  return (
    <Card
      className={styles.card}
      data-testid={`space-available-card-${space.slug}`}
      onClick={handleCardClick}
      style={{ cursor: 'pointer' }}
    >
      <div className={styles.cardHead}>
        <div className={styles.cardTitle}>
          <Link
            to={`/s/${space.slug}`}
            className={styles.cardTitleLink}
            title={space.name}
          >
            <SpaceAvatar
              name={space.name}
              icon={space.icon}
              seed={space.id}
              size={20}
              className="lens-space-av-inline"
            />
            <span className={styles.nameText}>{space.name}</span>
          </Link>
        </div>
      </div>
      <span className={styles.slug}>{space.slug}</span>
      {space.description ? (
        <Typography.Paragraph
          className={styles.description}
          ellipsis={{ rows: 2 }}
        >
          {space.description}
        </Typography.Paragraph>
      ) : (
        <div className={styles.descriptionEmpty}>Описание не задано</div>
      )}
    </Card>
  );
}

function RecentBlock({
  recent,
  knownIds,
}: {
  recent: RecentSpace[];
  knownIds: Set<string>;
}): JSX.Element | null {
  const navigate = useNavigate();
  // Показываем только те недавние, к которым у пользователя сейчас есть
  // доступ (есть в mine/available). Это честный фильтр: запись в localStorage
  // могла остаться после потери доступа.
  const items = recent.filter((r) => knownIds.has(r.id)).slice(0, 5);
  if (items.length === 0) return null;
  return (
    <div className={styles.recentBlock} data-testid="spaces-recent-section">
      <div className={styles.recentTitle}>
        <ClockCircleOutlined /> Недавнее
      </div>
      <div className={styles.recentList}>
        {items.map((r) => (
          <button
            type="button"
            key={r.id}
            className={styles.recentChip}
            onClick={() => navigate(`/s/${r.slug}`)}
            title={r.name}
          >
            <SpaceAvatar name={r.name} icon={r.icon} seed={r.id} size={20} />
            <span className={styles.recentName}>{r.name}</span>
            <span className={styles.recentSlug}>{r.slug}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

export function SpacesHomePage(): JSX.Element {
  useDocumentTitle('Пространства');
  const { user } = useAuth();
  const [state, setState] = useState<State>({
    data: null,
    loading: true,
    error: null,
  });
  const [modalOpen, setModalOpen] = useState(false);
  // Недавние пространства — вспомогательный блок поверх listSpaces. Читаем
  // синхронно из prefs-кэша один раз; безопасный parse внутри getRecentSpaces
  // деградирует в []. Это НЕ единственный источник истины о том, что показать.
  const [recent] = useState<RecentSpace[]>(() => getRecentSpaces());

  const load = useCallback(async () => {
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const data = await listSpaces();
      setState({ data, loading: false, error: null });
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.message || 'Не удалось загрузить список пространств'
          : 'Не удалось загрузить список пространств';
      setState({ data: null, loading: false, error: message });
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const handleCreated = useCallback((space: SpaceDto) => {
    setState((s) => ({
      ...s,
      data: s.data
        ? {
            ...s.data,
            mine: [...s.data.mine, { ...space }],
          }
        : { mine: [{ ...space }], available: [] },
    }));
    setModalOpen(false);
    // Левый рельс грузит пространства независимо — сообщаем ему обновиться.
    emitSpacesChanged();
  }, []);

  if (state.loading) {
    return (
      <div
        style={{ display: 'flex', justifyContent: 'center', padding: 64 }}
        data-testid="spaces-loading"
      >
        <Spin size="large" />
      </div>
    );
  }

  if (state.error) {
    return (
      <Alert
        type="error"
        message="Не удалось загрузить список пространств"
        description={state.error}
        showIcon
        data-testid="spaces-load-error"
        action={
          <Button
            size="small"
            onClick={() => void load()}
            data-testid="spaces-reload-button"
          >
            Повторить
          </Button>
        }
      />
    );
  }

  const mine = state.data?.mine ?? [];
  const available = state.data?.available ?? [];
  const knownIds = new Set<string>([
    ...mine.map((s) => s.id),
    ...available.map((s) => s.id),
  ]);

  return (
    <div className={styles.page}>
      <div className={styles.hero}>
        <div className={styles.heroText}>
          <h2 className={styles.pageTitle} data-testid="spaces-page-title">
            Пространства
          </h2>
          <div className={styles.heroSub}>
            Рабочие пространства для целей, инициатив и активностей
          </div>
        </div>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => setModalOpen(true)}
          data-testid="spaces-create-button"
        >
          Новое пространство
        </Button>
      </div>

      {/* «Недавнее» оправдано при многих пространствах; на малом числе оно
          лишь дублирует грид ниже — показываем только если их > 8. */}
      {mine.length + available.length > 8 ? (
        <RecentBlock recent={recent} knownIds={knownIds} />
      ) : null}

      {mine.length === 0 ? (
        <div className={styles.emptyState} data-testid="spaces-mine-empty">
          <div className={styles.emptyIcon}>
            <AppstoreOutlined />
          </div>
          <div className={styles.emptyTitle}>
            Вы ещё не состоите ни в одном пространстве
          </div>
          <div className={styles.emptySubtitle}>
            Создайте первое пространство для работы с целями и инициативами
          </div>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setModalOpen(true)}
          >
            Создать пространство
          </Button>
        </div>
      ) : (
        <div className={styles.grid}>
          {mine.map((space) => (
            <SpaceCard key={space.id} space={space} />
          ))}
        </div>
      )}

      {available.length > 0 ? (
        <div data-testid="spaces-available-section">
          <div className={styles.sectionHeader}>
            <div className={styles.sectionTitle}>
              Доступные пространства
            </div>
            <div className={styles.sectionSubtitle}>
              {user?.isPlatformAdmin
                ? 'Вы platform-админ и можете войти в любое из них'
                : 'Открытый доступ — вы можете работать в них как участник'}
            </div>
          </div>
          <div className={styles.grid}>
            {available.map((space) => (
              <AvailableSpaceCard key={space.id} space={space} />
            ))}
          </div>
        </div>
      ) : null}

      <CreateSpaceModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onCreated={handleCreated}
      />
    </div>
  );
}
